import { Component, Input, Pipe, PipeTransform } from '@angular/core';
import { MessageType } from '../../message.api.service';
import { MessageTypeNamePipe } from '../../message.pipe';

/** 资源类型转文案 Pipe：图片→[图片]，音频→[语音]，其余类型不显示文案 */
@Pipe({
  name: 'mediaLabel',
  standalone: true,
})
export class MediaLabelPipe implements PipeTransform {
  private static readonly IMAGE_EXT = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'avif'];
  private static readonly AUDIO_EXT = ['mp3', 'wav', 'amr', 'aac', 'm4a'];

  transform(url: string): string {
    const suffix = MediaLabelPipe.suffixOf(url);
    if (!suffix) return '';
    if (MediaLabelPipe.IMAGE_EXT.includes(suffix)) return '[图片]';
    if (MediaLabelPipe.AUDIO_EXT.includes(suffix)) return '[语音]';
    return '';
  }

  private static suffixOf(url: string): string {
    if (!url) return '';
    const clean = String(url).split('?')[0];
    const lastDot = clean.lastIndexOf('.');
    if (lastDot < 0) return '';
    return clean.slice(lastDot + 1).toLowerCase();
  }
}

/**
 * 单条消息摘要视图模型（聚合左栏/回复引用条共用）
 *
 * tags 只取展示所需的 firstName/tag，规避聚合接口 userId 与消息接口 user_id 的字段差异。
 */
export interface MessageSummary {
  /** 姓名 */
  firstName?: string;
  /** 正文 */
  content?: string;
  /** 消息类型（1 不懂 2 我来讲；0 默认不展示） */
  messageType?: MessageType;
  /** 标签（展示所需字段） */
  tags?: { firstName?: string; tag: string }[];
  /** 资源 URL（图片/语音等），按类型显示 [图片]/[语音] */
  mediaUrls?: string[];
  /** 可选引导词（如「回复」） */
  label?: string;
}

/**
 * 消息摘要展示：姓名：[标签胶囊] 正文
 *
 * 标签胶囊固定前置（无论正文是否为空），对齐消息详情里单标签按钮的视觉样式；
 * 姓名/正文颜色字号继承宿主管控，组件只负责结构。
 */
@Component({
  selector: 'fl-message-summary',
  template: `
    <span class="inline-flex max-w-full min-w-0 items-center gap-[4px]  text-[12px]">
      @if (summary.firstName) {
        <span class="shrink-0">{{ summary.label }} {{ summary.firstName }}：</span>
      }
      @if (summary.messageType | messageTypeName; as typeName) {
        <span class="shrink-0">[{{ typeName }}]</span>
      }
      @for (t of summary.tags ?? []; track $index) {
        <span class="shrink-0">[{{ t.tag }}]</span>
      }
      @if (summary.content) {
        <span class="truncate min-w-0">{{ summary.content }}</span>
      }
      @for (url of summary.mediaUrls ?? []; track url) {
        @if (url | mediaLabel; as label) {
          <span class="shrink-0">{{ label }}</span>
        }
      }
    </span>
  `,
  standalone: true,
  imports: [MediaLabelPipe, MessageTypeNamePipe],
})
export class MessageSummaryComponent {
  /** 摘要视图模型 */
  // TODO: Skipped for migration because:
  //  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
  //  and migrating would break narrowing currently.
  @Input() summary: MessageSummary = {};
}
